import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-store';

const STORAGE_KEY = 'eduborg.companion.visible';

/**
 * Whether the EduBorg companion is shown for the current user / org / plan.
 *
 * Visibility precedence (today):
 *   1. Anonymous → never visible.
 *   2. Per-user opt-out via localStorage (`eduborg.companion.visible = 'off'`)
 *      — set by clicking the toggle in the Dashboard welcome banner. K-12
 *      classes leave it on; HE instructors / adult learners can switch it off
 *      without affecting anyone else.
 *   3. (Phase 5+) per-org plan checks via `org_subscriptions.plan_features`.
 *   4. (Phase 5+) per-role kill switches (super_admin, admin, instructor, ...).
 *
 * Returns a `toggle()` action so the welcome-banner button can flip the flag
 * without re-implementing the localStorage dance.
 */
export function useEduBorgVisibility(): {
  visible: boolean;
  quietMode: boolean;
  toggle: () => void;
} {
  const { state } = useAuth();
  const [optOut, setOptOut] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === 'off';
  });

  // Sync across tabs of the same browser profile.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setOptOut(e.newValue === 'off');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const isAuthed = state.status === 'authenticated';
  return {
    visible: isAuthed && !optOut,
    quietMode: false,
    toggle: () => {
      const next = !optOut;
      setOptOut(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? 'off' : 'on');
      } catch {
        /* storage may be disabled in some embedded contexts */
      }
    },
  };
}
